import type React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { useProfile } from '@/hooks/use-profile'

/**
 * Requires authentication. Redirects to /login if not authenticated.
 * Stores the current URL for deep-link preservation.
 */
export function RequireAuth({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  const user = useAuthStore((s) => s.user)
  const initialized = useAuthStore((s) => s.initialized)
  const location = useLocation()

  if (!initialized) {
    return <div className="flex min-h-screen items-center justify-center text-neutral-500">Loading...</div>
  }

  if (!user) {
    // Store intended URL for redirect after login
    localStorage.setItem('knownow_redirect', location.pathname + location.search)
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

/**
 * Enforces onboarding gates:
 * - Incomplete profile → /onboarding/profile
 * - No org → /onboarding/org
 */
export function RequireOnboarding({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  const { profile, organizations, loading } = useProfile()

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-neutral-500">Loading...</div>
  }

  if (!profile?.completedAt) {
    return <Navigate to="/onboarding/profile" replace />
  }

  if (organizations.length === 0) {
    return <Navigate to="/onboarding/org" replace />
  }

  return <>{children}</>
}

/**
 * Redirects authenticated users away from login/signup pages.
 */
export function RedirectIfAuthenticated({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  const user = useAuthStore((s) => s.user)
  const initialized = useAuthStore((s) => s.initialized)

  if (!initialized) {
    return <div className="flex min-h-screen items-center justify-center text-neutral-500">Loading...</div>
  }

  if (user) {
    // Check for stored redirect URL
    const redirect = localStorage.getItem('knownow_redirect')
    if (redirect) {
      localStorage.removeItem('knownow_redirect')
      return <Navigate to={redirect} replace />
    }

    // Check for pending invite
    const inviteToken = localStorage.getItem('knownow_invite_token')
    if (inviteToken) {
      return <Navigate to={`/invite/${inviteToken}`} replace />
    }

    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
